import type { Client2TrackedTranslation } from '@ps/web-core-intl';
import type React from 'react';
import type { FieldMetaState, FieldProps, FieldRenderProps } from 'react-final-form';
import { Field } from 'react-final-form';

import type Translation from '../../Translation/Translation';
import withValidator, { type InjectedValidatorProps } from '../../hoc/withValidator';
import { usePauseValidator } from '../../hooks/usePauseValidator/usePauseValidator';
import type { ValuesProp } from '../../types/Props';
import RawInput from '../RawInput/RawInput';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface Props<V = any, T extends HTMLElement = HTMLInputElement>
  extends Omit<React.InputHTMLAttributes<T>, 'id'>,
    InjectedValidatorProps,
    Pick<FieldProps<V, FieldRenderProps<V, HTMLElement>>, 'parse' | 'format' | 'id'>,
    Partial<ValuesProp> {
  error?: boolean;
  helperMessage?: React.ReactNode;
  hideEye?: boolean;
  eyeButtonClassName?: string;
  hideLabel?: boolean;
  icon?: React.FC<React.SVGAttributes<SVGElement>> | React.ReactElement;
  inputRef?: React.RefObject<HTMLTextAreaElement | null> & React.RefObject<HTMLInputElement | null>;
  label: string | Client2TrackedTranslation;
  labelClassName?: string;
  labelContent?: React.ReactNode;
  meta?: FieldMetaState<string>;
  numericKeyboard?: boolean;
  rawValue?: string;
  recordValidator?: InjectedValidatorProps['validator'];
  showValidationTick?: boolean;
  startAdornment?: 'icon' | 'logo' | 'prefix' | 'logoAndPrefix';
  tip?: string;
  tooltip?: React.ComponentProps<typeof Translation>[];
  tooltipIconClassName?: string;
  tooltipClassName?: string;
  tooltipHover?: boolean;
  type?: 'date' | 'time' | 'text' | 'textarea' | 'password' | 'email' | 'number' | 'tel' | 'hidden';
  validationVariant?: Validation;
  value?: string;
  hideError?: boolean;
}

export enum Validation {
  ImmediateInline = 'immediateInline',
  ImmediateList = 'immediateList',
  Inline = 'inline',
  Invisible = 'invisible',
  List = 'list'
}

const Input: React.FC<Props> = (props) => {
  const { id, name, value, parse, format, recordValidator, validator } = props;
  usePauseValidator();

  return (
    <Field
      id={id}
      name={name || id}
      initialValue={value}
      component="input"
      parse={parse}
      format={format}
      validate={recordValidator || validator}
    >
      {({ input, meta }) => <RawInput {...props} input={input} meta={meta} />}
    </Field>
  );
};

export default withValidator(Input);
